import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../../components/Button";
import AdminSidebar from "../../components/AdminSidebar";
import {
  Upload,
  Database,
  X,
  FileText,
  Plus,
  ArrowLeft,
  BookOpen,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  fetchCustomModelById,
  updateCustomModel,
  fetchModels,
  fetchCustomModels,
  getKnowledgeBasesWithPromptTemplates,
  createModel,
} from "../../../services/modelApi";
import { SearchableDropdown } from "../../components/SearchableDropdown";

import FolderModal from "../../components/FolderModal";
import KnowledgeBaseModal from "../../components/KnowledgeBaseModal";

function ModelEdit() {
  const { modelId } = useParams();
  const navigate = useNavigate();

  const [modelHandle, setModelHandle] = useState(undefined);
  const [modelTitle, setModelTitle] = useState("");
  const [modelDescription, setModelDescription] = useState("");
  const [modelFolder, setModelFolder] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [transcriptFiles, setTranscriptFiles] = useState([]); // New files to add
  const [existingFiles, setExistingFiles] = useState([]); // Existing files
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [updateLoading, setUpdateLoading] = useState(false);
  const [indexes, setIndexes] = useState([]);
  const [allIndexes, setAllIndexes] = useState([]);
  const [botGreeting, setBotGreeting] = useState("");
  const [internetAccess, setInternetAccess] = useState(false);
  const [folders, setFolders] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [folderSearchLoading, setFolderSearchLoading] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] =
    useState(false);
  const [conversationStarters, setConversationStarters] = useState([]);
  const [starterInput, setStarterInput] = useState("");

  // Prompt RAG state variables
  const [promptRAGEnabled, setPromptRAGEnabled] = useState(false);
  const [promptStrategy, setPromptStrategy] = useState("enhance");
  const [promptTemplateFiles, setPromptTemplateFiles] = useState([]);
  const [existingPromptTemplateFiles, setExistingPromptTemplateFiles] =
    useState([]);
  const [selectedPromptKnowledgeBase, setSelectedPromptKnowledgeBase] =
    useState(null);
  const [promptKnowledgeBases, setPromptKnowledgeBases] = useState([]);
  const [allPromptKnowledgeBases, setAllPromptKnowledgeBases] = useState([]);
  const [
    promptKnowledgeBaseSearchLoading,
    setPromptKnowledgeBaseSearchLoading,
  ] = useState(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Validate file types
    const validFiles = selectedFiles.filter((file) => {
      const validTypes = [".txt", ".pdf", ".json", ".docx"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      return validTypes.includes(fileExtension);
    });

    if (validFiles.length !== selectedFiles.length) {
      toast.error(
        "Some files were rejected. Only .txt, .pdf, .docx and .json files are allowed."
      );
    }

    // Add new files to existing ones (avoiding duplicates)
    setTranscriptFiles((prevFiles) => {
      const existingFileNames = prevFiles.map((f) => f.name);
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.includes(file.name)
      );

      if (newFiles.length === 0 && validFiles.length > 0) {
        toast.info("Some files were already selected.");
      }

      return [...prevFiles, ...newFiles];
    });

    setErrors((p) => ({ ...p, transcript: undefined }));
    // Clear the input to allow selecting the same file again if needed
    e.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setTranscriptFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const removeAllFiles = () => {
    setTranscriptFiles([]);
  };

  // Prompt template file handlers
  const handlePromptFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const validFiles = selectedFiles.filter((file) => {
      const isValidType =
        file.name.endsWith(".txt") ||
        file.name.endsWith(".json") ||
        file.name.endsWith(".pdf") ||
        file.name.endsWith(".docx");
      const isValidSize = file.size < 10 * 1024 * 1024; // 10MB limit

      if (!isValidType) {
        toast.error(
          `File "${file.name}" must be a .txt, .json, .docx or .pdf file`
        );
        return false;
      }

      if (!isValidSize) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return false;
      }

      return true;
    });

    setPromptTemplateFiles((prevFiles) => {
      const existingNames = prevFiles.map((f) => f.name);
      const newFiles = validFiles.filter(
        (file) => !existingNames.includes(file.name)
      );

      if (newFiles.length === 0 && validFiles.length > 0) {
        toast.info("Some files were already selected.");
      }

      return [...prevFiles, ...newFiles];
    });

    e.target.value = "";
  };

  const removePromptFile = (indexToRemove) => {
    setPromptTemplateFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const removeAllPromptFiles = () => {
    setPromptTemplateFiles([]);
  };

  const fetchPromptKnowledgeBases = async () => {
    try {
      const res = await getKnowledgeBasesWithPromptTemplates();
      const kbOptions = res.data.map((kb, index) => ({
        label: kb.label, // Use the label from API which includes the prompt namespace name
        value: kb.value, // Use the value from API (original kb name for API calls)
        id: index + 1,
        templateCount: kb.templateCount,
        promptNamespace: kb.promptNamespace,
        originalKbName: kb.originalKbName,
      }));
      setPromptKnowledgeBases(kbOptions);
      setAllPromptKnowledgeBases(kbOptions);
    } catch (error) {
      console.error("Error fetching prompt knowledge bases:", error);
      setPromptKnowledgeBases([]);
      setAllPromptKnowledgeBases([]);
    }
  };

  const handlePromptKnowledgeBaseSearch = (query) => {
    setPromptKnowledgeBaseSearchLoading(true);
    setTimeout(() => {
      const allOptions = [...allPromptKnowledgeBases];
      if (query) {
        const filtered = allOptions.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
        setPromptKnowledgeBases(filtered);
      } else {
        setPromptKnowledgeBases(allOptions);
      }
      setPromptKnowledgeBaseSearchLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchModel();
    fetchHandles();
    fetchFolders();
    fetchPromptKnowledgeBases();
  }, [modelId]);

  const fetchFolders = async () => {
    try {
      // Fetch existing models to get their folder information
      const res = await fetchCustomModels();

      // Extract unique folders from existing models
      const existingFolders = new Set();

      // Add folders from existing models
      res.data.forEach((model) => {
        if (model.folder && model.folder.trim() !== "") {
          existingFolders.add(model.folder);
        }
      });

      // Convert to folder objects
      const allFolders = Array.from(existingFolders).map(
        (folderValue, index) => {
          // For custom folders, create a label from the value
          const label = folderValue
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          return {
            label: label,
            value: folderValue,
            id: index + 1,
          };
        }
      );

      setFolders(allFolders);
      setAllFolders(allFolders);
    } catch (error) {
      console.error("Error fetching folders from models:", error);
      // Fallback to empty array if fetching fails
      setFolders([]);
      setAllFolders([]);
    }
  };

  const fetchModel = async () => {
    try {
      setLoading(true);
      const res = await fetchCustomModelById(modelId);
      const model = res.data;

      setModelTitle(model.name || "");
      setModelDescription(model.description || "");
      setConversationStarters(model.conversationStarters || []);
      setSelectedPromptKnowledgeBase({
        label: `${model.name}-prompts (1 templates)`,
        value: model.name,
        promptNamespace: model.promptNamespace,
        originalKbName: model.name,
      });

      // Set folder as object for dropdown
      if (model.folder && model.folder.trim() !== "") {
        const folderLabel = model.folder
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        setModelFolder({ label: folderLabel, value: model.folder });
      } else {
        setModelFolder(null);
      }

      setSystemPrompt(model.systemPrompt || "");
      setExistingFiles(model.files || []);

      // Set model handle
      const handleOption = {
        label: model.modelHandle,
        value: model.modelHandle,
      };
      setModelHandle(handleOption);

      // Extract greeting from model
      console.log("Model data received:", model);
      setBotGreeting(model.greeting || "");

      // Set internet access
      setInternetAccess(model.internetAccess || false);

      // Set prompt RAG configuration
      setPromptRAGEnabled(model.promptRAGEnabled || false);
      setPromptStrategy(model.promptStrategy || "enhance");
      setExistingPromptTemplateFiles(model.promptTemplateFiles || []);
    } catch {
      toast.error("Failed to fetch model data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateModel = async () => {
    try {
      setUpdateLoading(true);

      // Clear previous errors
      setErrors({});

      // Validate required fields
      const newErrors = {};

      if (!modelTitle || modelTitle.trim() === "") {
        newErrors.modelTitle = "Bot name is required";
      }

      if (!modelHandle) {
        newErrors.model = "Please select a knowledge base";
      }

      // If there are validation errors, show them and return
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error("Please fill in all required fields");
        setUpdateLoading(false);
        return;
      }

      const updateData = {
        _id: modelId,
        title: modelTitle,
        description: modelDescription,
        folder: modelFolder?.value || "",
        systemPrompt: systemPrompt,
        modelHandle: modelHandle.value,
        greeting: botGreeting.trim() || "",
        internetAccess: internetAccess,
        conversationStarters: conversationStarters,
        promptRAGEnabled: promptRAGEnabled,
        promptStrategy: promptStrategy,
        promptNamespace:
          promptRAGEnabled && selectedPromptKnowledgeBase
            ? selectedPromptKnowledgeBase.promptNamespace
            : promptRAGEnabled
            ? `${modelHandle.value.toLowerCase().trim()}-prompts`
            : null,
      };

      // Add new transcript files if any
      let formData = new FormData();

      if (transcriptFiles.length > 0 || promptTemplateFiles.length > 0) {
        transcriptFiles.forEach((file) => {
          formData.append("transcriptFiles", file);
        });

        // Add prompt template files if prompt RAG is enabled
        if (promptRAGEnabled) {
          promptTemplateFiles.forEach((file) => {
            formData.append("promptTemplateFiles", file);
          });

          // Add selected existing prompt knowledge base
          if (selectedPromptKnowledgeBase) {
            formData.append(
              "existingPromptKnowledgeBase",
              selectedPromptKnowledgeBase.value
            );
          }
        }

        // formData.append("modelHandle", modelHandle.value);
        // formData.append("conversationStarters", conversationStarters);
        // You'll need to create an endpoint for adding files to existing models
        // await addFilesToModel(modelId, formData)
      }

      Object.keys(updateData).map((key) => {
        formData.append(key, updateData[key]);
      });
      await createModel(formData);

      toast.success("Model updated successfully!");
      navigate("/admin-dashboard/model-list");
    } catch (error) {
      toast.error(error.message || "Failed to update model");
    } finally {
      setUpdateLoading(false);
    }
  };

  const fetchHandles = async () => {
    try {
      const res = await fetchModels();
      setIndexes(
        res.data.map((item, i) => ({
          label: item.name,
          value: item.name,
          id: i + 1,
        }))
      );
      setAllIndexes(
        res.data.map((item, i) => ({
          label: item.name,
          value: item.name,
          id: i + 1,
        }))
      );
    } catch (error) {
      toast.error(error);
    }
  };

  const handleSearch = (query) => {
    // Simulate API call
    setTimeout(() => {
      const allOptions = [...allIndexes];
      if (query) {
        const filtered = allOptions.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
        setIndexes(filtered);
      } else {
        setIndexes(allOptions);
      }
    }, 500);
  };

  const handleFolderSearch = (query) => {
    setFolderSearchLoading(true);
    // Simulate API call
    setTimeout(() => {
      const allOptions = [...allFolders];
      if (query) {
        const filtered = allOptions.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
        setFolders(filtered);
      } else {
        setFolders(allOptions);
      }
      setFolderSearchLoading(false);
    }, 500);
  };

  const handleFolderModalSave = async (folderData) => {
    try {
      // Create new folder object
      const newFolder = {
        label: folderData.name,
        value: folderData.name.toLowerCase().replace(/\s+/g, "-"),
        id: allFolders.length + 1,
      };

      // Add to the current folders list
      const updatedFolders = [...allFolders, newFolder];

      // Update the state
      setFolders(updatedFolders);
      setAllFolders(updatedFolders);
      setModelFolder(newFolder);
      toast.success("Folder created successfully");
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error("Failed to create folder");
      throw error;
    }
  };

  const handleKnowledgeBaseModalSave = async (knowledgeBaseData) => {
    try {
      // Create new knowledge base object
      const newKnowledgeBase = {
        label: knowledgeBaseData.name,
        value: knowledgeBaseData.name,
        id: indexes.length + 1,
      };

      // Add to the current indexes list
      const updatedIndexes = [...indexes, newKnowledgeBase];
      const updatedAllIndexes = [...allIndexes, newKnowledgeBase];

      // Update the state
      setIndexes(updatedIndexes);
      setAllIndexes(updatedAllIndexes);
      setModelHandle(newKnowledgeBase);
      toast.success("Knowledge base created successfully");
    } catch (error) {
      console.error("Error creating knowledge base:", error);
      toast.error("Failed to create knowledge base");
      throw error;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (loading) {
    return (
      <div className="w-screen min-h-screen bg-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="w-screen min-h-screen bg-gray-800 overflow-x-hidden">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <div className="hidden lg:block w-64 h-full">
          <AdminSidebar />
        </div>
        {/* Main Content */}
        <div className="flex-1 flex flex-col h-full overflow-y-auto bg-gray-800">
          {/* Header */}
          <div className="px-8 py-6 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/admin-dashboard/model-list")}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <Database className="text-blue-400" size={24} />
              <div>
                <h1 className="text-2xl font-semibold text-white">
                  Edit AI Model
                </h1>
                <p className="text-gray-400 text-sm">
                  Update model details and add more knowledge
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-3 max-w-7xl  mx-auto p-2 lg:p-8">
            <div className=" bg-gray-900 border border-gray-700 rounded-xl p-8">
              <div className="flex  items-center gap-3 mb-8">
                <Database className="text-blue-400" size={24} />
                <h2 className="text-lg font-semibold text-white">
                  Model Configuration
                </h2>
              </div>

              {/* Required Fields Summary */}
              <div className="mb-6 p-4 bg-blue-900 bg-opacity-20 border border-blue-600 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-blue-300 flex items-center gap-2">
                    <span>ℹ</span>
                    Required Fields
                  </h3>
                  <div className="text-xs text-blue-200">
                    {(() => {
                      const completed = [
                        modelTitle.trim() !== "",
                        !!modelHandle,
                      ].filter(Boolean).length;
                      const total = 2;
                      return `${completed}/${total} completed`;
                    })()}
                  </div>
                </div>
                <ul className="text-xs text-blue-200 space-y-1">
                  <li
                    className={`flex items-center gap-2 ${
                      modelTitle.trim() !== "" ? "text-green-300" : ""
                    }`}
                  >
                    <span>{modelTitle.trim() !== "" ? "✓" : "•"}</span>
                    <strong>Name:</strong> The name of your AI bot
                  </li>
                  <li
                    className={`flex items-center gap-2 ${
                      modelHandle ? "text-green-300" : ""
                    }`}
                  >
                    <span>{modelHandle ? "✓" : "•"}</span>
                    <strong>Knowledge Base:</strong> Select or create a
                    knowledge base for your bot
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-800 text-white placeholder:text-gray-400 ${
                    errors.modelTitle
                      ? "border-red-500 focus:ring-red-500"
                      : modelTitle.trim() === ""
                      ? "border-yellow-500"
                      : "border-gray-600"
                  }`}
                  value={modelTitle}
                  onChange={(e) => {
                    setModelTitle(e.target.value);
                    setErrors((p) => ({ ...p, modelTitle: undefined }));
                  }}
                  placeholder="Enter bot name (required)"
                  disabled={loading || updateLoading}
                />
                {errors.modelTitle && (
                  <small className="mt-1 text-red-500 flex items-center gap-1">
                    <span>⚠</span>
                    {errors.modelTitle}
                  </small>
                )}
                {!errors.modelTitle && modelTitle.trim() === "" && (
                  <small className="mt-1 text-yellow-500 flex items-center gap-1">
                    <span>ℹ</span>
                    This field is required
                  </small>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Description
                </label>
                <textarea
                  className="w-full border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-800 text-white placeholder:text-gray-400 resize-none"
                  value={modelDescription}
                  onChange={(e) => {
                    setModelDescription(e.target.value);
                    setErrors((p) => ({ ...p, modelDescription: undefined }));
                  }}
                  placeholder="Enter bot description"
                  disabled={loading || updateLoading}
                  rows={3}
                />
                {errors.modelDescription && (
                  <small className="mt-1 text-red-600">
                    {errors.modelDescription}
                  </small>
                )}
              </div>

              <div className="mb-6 ">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Folder
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableDropdown
                      showAddButton={false}
                      className="w-full"
                      valueClassName="border border-gray-600 px-3 py-2 w-full bg-gray-800 text-white"
                      value={modelFolder}
                      onChange={(value) => {
                        setModelFolder(value);
                        setErrors((p) => ({ ...p, modelFolder: undefined }));
                      }}
                      onSearch={handleFolderSearch}
                      options={folders}
                      loading={folderSearchLoading}
                      placeholder="Select a folder..."
                      searchPlaceholder="Search folders..."
                      theme="dark"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsFolderModalOpen(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    New Folder
                  </button>
                </div>
                {errors.modelFolder && (
                  <small className="mt-1 text-red-600">
                    {errors.modelFolder}
                  </small>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Prompt
                </label>
                <textarea
                  className="w-full border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-800 text-white placeholder:text-gray-400 resize-none"
                  value={systemPrompt}
                  onChange={(e) => {
                    setSystemPrompt(e.target.value);
                    setErrors((p) => ({ ...p, systemPrompt: undefined }));
                  }}
                  placeholder="Enter system instruction prompt"
                  disabled={loading || updateLoading}
                  rows={4}
                />
                {errors.systemPrompt && (
                  <small className="mt-1 text-red-600">
                    {errors.systemPrompt}
                  </small>
                )}
              </div>
            </div>
            {/* second part  */}
            <div className=" bg-gray-900 border border-gray-700 rounded-xl p-8">
              <div className="flex items-center gap-3 mb-8">
                <BookOpen className="text-blue-400" size={24} />
                <h2 className="text-lg font-semibold text-white">
                  Knowledge Base Configuration
                </h2>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Knowledge Base <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableDropdown
                      showAddButton={false}
                      className="w-full"
                      valueClassName={`px-3 py-2 w-full bg-gray-800 text-white ${
                        errors.model
                          ? "border border-red-500"
                          : !modelHandle
                          ? "border border-yellow-500"
                          : "border border-gray-600"
                      }`}
                      value={modelHandle}
                      onChange={(value) => {
                        setModelHandle(value);
                        setErrors((p) => ({ ...p, model: undefined }));
                      }}
                      onSearch={handleSearch}
                      options={indexes}
                      loading={false}
                      placeholder="Select a knowledge base (required)..."
                      searchPlaceholder="Search knowledge bases..."
                      theme="dark"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsKnowledgeBaseModalOpen(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus size={16} />
                    New
                  </button>
                </div>
                {errors.model && (
                  <small className="mt-1 text-red-500 flex items-center gap-1">
                    <span>⚠</span>
                    {errors.model}
                  </small>
                )}
                {!errors.model && !modelHandle && (
                  <small className="mt-1 text-yellow-500 flex items-center gap-1">
                    <span>ℹ</span>
                    Please select a knowledge base
                  </small>
                )}
              </div>

              {/* Existing Files Section */}
              {existingFiles.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2 text-gray-300">
                    Existing Knowledge Base Files
                  </label>
                  <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-300 mb-3">
                      Current Files:
                    </h4>
                    <div className="space-y-2">
                      {existingFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-700 p-3 rounded border border-gray-600"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText
                              className="text-blue-400 flex-shrink-0"
                              size={16}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-300">
                    Add More Knowledge Base Files
                  </label>
                  {transcriptFiles.length > 0 && (
                    <button
                      type="button"
                      onClick={removeAllFiles}
                      className="text-xs text-red-400 hover:text-red-300 font-medium"
                      disabled={loading || updateLoading}
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* File Upload Button */}
                <div className="flex items-center gap-3 ">
                  <label className="flex gap-2 w-full items-center justify-center cursor-pointer px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 transition">
                    <Upload className="text-blue-400" size={20} />
                    <span className="text-sm font-medium text-gray-300">
                      {transcriptFiles.length === 0
                        ? "Choose Files"
                        : "Add More Files"}
                    </span>
                    <input
                      type="file"
                      accept=".txt,.pdf,.json,.docx"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={loading || updateLoading}
                      multiple
                    />
                  </label>
                  {transcriptFiles.length > 0 && (
                    <span className="text-sm text-blue-400 font-medium">
                      {transcriptFiles.length} file
                      {transcriptFiles.length !== 1 ? "s" : ""} selected
                    </span>
                  )}
                </div>
                <div>
                  {errors.transcript && (
                    <small className=" text-red-600">{errors.transcript}</small>
                  )}
                </div>

                {/* Selected Files List */}
                {transcriptFiles.length > 0 && (
                  <div className="bg-gray-800 border border-gray-600 rounded-lg p-4 max-h-48 overflow-y-auto">
                    <h4 className="text-sm font-semibold text-gray-300 mb-3">
                      Selected Files:
                    </h4>
                    <div className="space-y-2">
                      {transcriptFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-700 p-3 rounded border border-gray-600"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText
                              className="text-blue-400 flex-shrink-0"
                              size={16}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-red-500/20 transition-colors flex-shrink-0"
                            disabled={loading || updateLoading}
                            title="Remove file"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* conversation starter Section */}

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-300">
                  Conversation Starters
                </label>
                <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add a starter, e.g., “What can you do?”"
                      value={starterInput}
                      onChange={(e) => setStarterInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && starterInput.trim()) {
                          setConversationStarters((prev) => [
                            ...prev,
                            starterInput.trim(),
                          ]);
                          setStarterInput("");
                        }
                      }}
                      disabled={updateLoading}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (starterInput.trim()) {
                          setConversationStarters((prev) => [
                            ...prev,
                            starterInput.trim(),
                          ]);
                          setStarterInput("");
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      disabled={updateLoading}
                    >
                      Add
                    </button>
                  </div>

                  {conversationStarters?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {conversationStarters?.map((s, i) => (
                        <span
                          key={`${s}-${i}`}
                          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-700 text-gray-100 text-xs border border-gray-600"
                        >
                          {s}
                          <button
                            type="button"
                            className="text-red-300 hover:text-red-200"
                            onClick={() =>
                              setConversationStarters((prev) =>
                                prev.filter((_, idx) => idx !== i)
                              )
                            }
                            disabled={updateLoading}
                            title="Remove starter"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {conversationStarters?.length === 0 && (
                    <p className="mt-2 text-xs text-gray-400">
                      Add short prompts to help users start the conversation.
                      You can add multiple.
                    </p>
                  )}
                </div>
              </div>
              {/* Internet Access Toggle */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-300">
                    Internet Access
                  </label>
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        internetAccess
                          ? "bg-green-500/20 text-green-400"
                          : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {internetAccess ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-3">
                  When enabled, this bot can access the internet to search for
                  current information and browse websites. When disabled, the
                  bot will only use its knowledge base and cannot access
                  external information.
                </p>

                <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Allow Internet Access
                      </label>
                      <p className="text-xs text-gray-400">
                        {internetAccess
                          ? "Bot can search the web and access current information"
                          : "Bot is restricted to knowledge base only"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setInternetAccess(!internetAccess)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                        internetAccess ? "bg-blue-600" : "bg-gray-600"
                      }`}
                      disabled={updateLoading}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          internetAccess ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Prompt RAG Configuration */}
              <div className="mb-6">
                <div className="flex flex-col lg:flex-row items-start gap-2 lg:items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-gray-300">
                    Prompt Optimization (Beta)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={promptRAGEnabled}
                      onChange={(e) => setPromptRAGEnabled(e.target.checked)}
                      className="rounded"
                      disabled={updateLoading}
                    />
                    <span className="text-sm text-gray-400">
                      Enable prompt optimization
                    </span>
                  </div>
                </div>

                <div className="text-xs text-gray-400 mb-3">
                  Upload prompt templates to optimize AI responses based on
                  context and query patterns. This feature enhances response
                  quality by using contextual prompt selection.
                </div>

                {promptRAGEnabled && (
                  <>
                    {/* Strategy Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Optimization Strategy
                      </label>
                      <select
                        value={promptStrategy}
                        onChange={(e) => setPromptStrategy(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        disabled={updateLoading}
                      >
                        <option value="enhance">
                          Enhance - Add context to existing prompt
                        </option>
                        <option value="optimize">
                          Optimize - Use template-based prompting
                        </option>
                        <option value="hybrid">
                          Hybrid - Combine templates with base prompt
                        </option>
                      </select>
                      <div className="text-xs text-gray-400 mt-1">
                        {promptStrategy === "enhance" &&
                          "Adds contextual information to your existing system prompt"}
                        {promptStrategy === "optimize" &&
                          "Replaces system prompt with optimized templates based on query"}
                        {promptStrategy === "hybrid" &&
                          "Intelligently merges templates with your base prompt"}
                      </div>
                    </div>

                    {/* Existing Prompt Template Files */}
                    {existingPromptTemplateFiles.length > 0 && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Existing Prompt Templates
                        </label>
                        <div className="bg-gray-800 border border-purple-600 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-gray-300 mb-3">
                            Current Templates:
                          </h4>
                          <div className="space-y-2">
                            {existingPromptTemplateFiles.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-gray-700 p-2 rounded border border-gray-600"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <FileText
                                    className="text-purple-400 flex-shrink-0"
                                    size={14}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-white truncate">
                                      {file.name}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      {formatFileSize(file.size)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Existing Prompt Knowledge Base Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Select Existing Prompt Templates (Optional)
                      </label>
                      <SearchableDropdown
                        showAddButton={false}
                        className="w-full"
                        valueClassName="border border-purple-600 px-3 py-2 w-full bg-gray-800 text-white"
                        value={selectedPromptKnowledgeBase}
                        onChange={(value) => {
                          setSelectedPromptKnowledgeBase(value);
                        }}
                        onSearch={handlePromptKnowledgeBaseSearch}
                        options={promptKnowledgeBases}
                        loading={promptKnowledgeBaseSearchLoading}
                        placeholder="Select existing prompt templates..."
                        searchPlaceholder="Search prompt knowledge bases..."
                        theme="dark"
                      />
                      <div className="mt-2 text-xs text-gray-400">
                        Choose from existing knowledge bases that contain prompt
                        templates, or upload new ones below.
                      </div>
                    </div>

                    {/* Prompt Template Upload */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-300">
                          Add More Prompt Templates
                        </label>
                        {promptTemplateFiles.length > 0 && (
                          <button
                            type="button"
                            onClick={removeAllPromptFiles}
                            className="text-xs text-red-400 hover:text-red-300 font-medium"
                            disabled={updateLoading}
                          >
                            Clear All
                          </button>
                        )}
                      </div>

                      <label className="flex gap-2 w-full items-center justify-center cursor-pointer px-4 py-3 bg-gray-800 border border-purple-600 rounded-lg hover:bg-gray-700 transition">
                        <Upload className="text-purple-400" size={20} />
                        <span className="text-sm font-medium text-gray-300">
                          {promptTemplateFiles.length === 0
                            ? "Upload Prompt Templates (.txt, .json, .pdf, .docx)"
                            : "Add More Templates"}
                        </span>
                        <input
                          type="file"
                          accept=".txt,.json,.pdf,.docx"
                          multiple
                          className="hidden"
                          onChange={handlePromptFileChange}
                          disabled={updateLoading}
                        />
                      </label>

                      <div className="mt-2 text-xs text-gray-400">
                        <span>
                          Maximum file size: 10MB per file. (.txt, .json, .pdf,
                          .docx)
                        </span>
                      </div>

                      {promptTemplateFiles.length > 0 && (
                        <>
                          <span className="text-sm text-purple-400 font-medium mt-2 block">
                            {promptTemplateFiles.length} template
                            {promptTemplateFiles.length !== 1 ? "s" : ""}{" "}
                            selected
                          </span>

                          {/* Selected Template Files */}
                          <div className="bg-gray-800 border border-purple-600 rounded-lg p-4 max-h-32 overflow-y-auto mt-3">
                            <h4 className="text-sm font-semibold text-gray-300 mb-3">
                              New Templates:
                            </h4>
                            <div className="space-y-2">
                              {promptTemplateFiles.map((file, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between bg-gray-700 p-2 rounded border border-gray-600"
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <FileText
                                      className="text-purple-400 flex-shrink-0"
                                      size={14}
                                    />
                                    <span className="text-sm text-white truncate">
                                      {file.name}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removePromptFile(index)}
                                    className="text-red-400 hover:text-red-300 p-1"
                                    disabled={updateLoading}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Info Box */}
                    <div className="bg-purple-900/20 border border-purple-600/30 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-purple-300 mb-1">
                            How Prompt Templates Work
                          </h3>
                          <ul className="text-xs text-gray-300 space-y-1">
                            <li>
                              • <strong>System templates:</strong> Replace or
                              enhance your base system prompt
                            </li>
                            <li>
                              • <strong>Context templates:</strong> Improve how
                              the AI processes retrieved information
                            </li>
                            <li>
                              • <strong>User templates:</strong> Enhance user
                              interaction patterns
                            </li>
                            <li>
                              • Templates are automatically categorized based on
                              content
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <Button
                name="Update Bot"
                onClick={handleUpdateModel}
                isLoading={updateLoading}
                type="secondary"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                disabled={updateLoading || !modelTitle || !modelHandle}
                icon={<Database size={20} />}
              />
            </div>
          </div>
        </div>
      </div>
      <FolderModal
        isOpen={isFolderModalOpen}
        onClose={() => setIsFolderModalOpen(false)}
        onSave={handleFolderModalSave}
        allFolders={allFolders}
      />
      <KnowledgeBaseModal
        isOpen={isKnowledgeBaseModalOpen}
        onClose={() => setIsKnowledgeBaseModalOpen(false)}
        onSave={handleKnowledgeBaseModalSave}
        allIndexes={allIndexes}
      />
    </div>
  );
}

export default ModelEdit;
